import isEqual from 'lodash/isEqual';
import type { OperationType, GraphQLTaggedNode, FetchPolicy } from 'relay-runtime';
import { v4 as uuid } from 'uuid';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { Action } from '@atlassian/react-sweet-state';
import type { State } from './types.tsx';

type Config = {
	id: string;
	query: GraphQLTaggedNode;
	variables: OperationType['variables'];
	skip: boolean;
	fetchPolicy: FetchPolicy | typeof undefined;
};
type RegisterOrUpdateQueryAction = (config: Config) => Action<State>;
export const registerOrUpdateQuery: RegisterOrUpdateQueryAction =
	(config: Config) =>
	({ getState, setState }) => {
		const state = getState();

		const existingQueryConfig = state.queries.find((q) => q.id === config.id);

		if (existingQueryConfig) {
			setState({
				queries: state.queries.map((q) => {
					if (q.id === config.id) {
						return {
							...q,
							...config,
						};
					}
					return q;
				}),
			});
		} else {
			setState({
				queries: state.queries.concat({
					...config,
					fetchKey: config.id,
					result: {
						data: null,
						error: undefined,
						loading: true,
					},
				}),
			});
		}
	};

type SetQueryDataAction = (id: string, data: OperationType['response']) => Action<State>;
export const setQueryData: SetQueryDataAction =
	(id: string, data: OperationType['response']) =>
	({ getState, setState }) => {
		const { queries } = getState();

		setState({
			queries: queries.map((item) =>
				item.id === id
					? {
							...item,
							result: {
								loading: false,
								error: undefined,
								data,
							},
						}
					: item,
			),
		});
	};

type SetQueryLoadingAction = (id: string) => Action<State>;
export const setQueryLoading: SetQueryLoadingAction =
	(id: string) =>
	({ getState, setState }) => {
		const { queries } = getState();

		setState({
			queries: queries.map((item) =>
				item.id === id
					? {
							...item,
							result: {
								loading: true,
								error: undefined,
								data: null,
							},
						}
					: item,
			),
		});
	};

type SetQueryErrorAction = (id: string, error: Error) => Action<State>;
export const setQueryError: SetQueryErrorAction =
	(id: string, error: Error | typeof undefined) =>
	({ getState, setState }) => {
		const { queries } = getState();

		setState({
			queries: queries.map((item) =>
				item.id === id
					? {
							...item,
							result: {
								loading: false,
								error,
								data: null,
							},
						}
					: item,
			),
		});
	};

type RemoveQueryAction = (id: string) => Action<State>;
export const removeQuery: RemoveQueryAction =
	(id: string) =>
	({ getState, setState }) => {
		const { queries } = getState();

		setState({
			queries: queries.filter((item) => item.id !== id),
		});
	};

type UpdatedProps = {
	variables?: OperationType['variables'];
	skip?: boolean;
	fetchKey?: string;
	fetchPolicy?: FetchPolicy | typeof undefined;
};
type UpdateQueryAction = (id: string, props: UpdatedProps) => Action<State>;

const updateQuery: UpdateQueryAction =
	(id: string, props: UpdatedProps) =>
	({ getState, setState }) => {
		const { queries } = getState();

		setState({
			queries: queries.map((item) =>
				item.id === id
					? {
							...item,
							...props,
						}
					: item,
			),
		});
	};

type ForceCacheRecheckAction = (arg1: {
	query: GraphQLTaggedNode;
	variables: OperationType['variables'];
}) => Action<State>;

const forceCacheRecheck: ForceCacheRecheckAction =
	({ query, variables }: { query: GraphQLTaggedNode; variables: OperationType['variables'] }) =>
	({ getState, setState }) => {
		const { queries } = getState();

		setState({
			queries: queries.map((item) =>
				item.query === query && isEqual(item.variables, variables)
					? {
							...item,
							fetchKey: `query-key-${uuid()}`,
						}
					: item,
			),
		});
	};

export const actions = {
	registerOrUpdateQuery,
	setQueryData,
	setQueryLoading,
	setQueryError,
	removeQuery,
	updateQuery,
	forceCacheRecheck,
} as const;

export type Actions = {
	registerOrUpdateQuery: RegisterOrUpdateQueryAction;
	setQueryData: SetQueryDataAction;
	setQueryLoading: SetQueryLoadingAction;
	setQueryError: SetQueryErrorAction;
	removeQuery: RemoveQueryAction;
	updateQuery: UpdateQueryAction;
	forceCacheRecheck: ForceCacheRecheckAction;
};
